/** Query key for in-page section anchors (legacy HashRouter-safe bookmarks). */
export const DOC_SECTION_SEARCH_PARAM = "section";

/** postMessage envelope from Markdoc iframe → SPA parent. */
export const DOCS_FRAME_MESSAGE_SOURCE = "api-portal-docs";
export const DOCS_FRAME_SECTION_MESSAGE_TYPE = "section";
export const DOCS_FRAME_ROUTE_MESSAGE_TYPE = "route";

export interface DocsFrameSectionMessage {
  source: typeof DOCS_FRAME_MESSAGE_SOURCE;
  type: typeof DOCS_FRAME_SECTION_MESSAGE_TYPE;
  id: string | null;
}

export interface DocsFrameRouteMessage {
  source: typeof DOCS_FRAME_MESSAGE_SOURCE;
  type: typeof DOCS_FRAME_ROUTE_MESSAGE_TYPE;
  pathname: string;
  search?: string;
  hash?: string;
}

/** HashRouter location parsed from a portal hash link (`/api-portal/#/glofox/…`). */
export function parsePortalHashHref(
  href: string,
): Pick<DocsFrameRouteMessage, "pathname" | "search" | "hash"> | null {
  const trimmed = href.trim();
  if (!trimmed) return null;

  let hashRoute = "";
  if (trimmed.startsWith("#/")) {
    hashRoute = trimmed;
  } else {
    const portalPrefix = "/api-portal/#";
    const portalIndex = trimmed.indexOf(portalPrefix);
    if (portalIndex === -1) return null;
    hashRoute = `#${trimmed.slice(portalIndex + portalPrefix.length)}`;
  }

  if (!hashRoute.startsWith("#/")) return null;

  const rest = hashRoute.slice(1);
  let pathname = rest;
  let search = "";
  let sectionHash = "";

  const queryIndex = rest.indexOf("?");
  if (queryIndex !== -1) {
    pathname = rest.slice(0, queryIndex);
    const afterQuery = rest.slice(queryIndex);
    const hashInQuery = afterQuery.indexOf("#");
    search = hashInQuery === -1 ? afterQuery : afterQuery.slice(0, hashInQuery);
    if (hashInQuery !== -1) {
      sectionHash = afterQuery.slice(hashInQuery);
    }
  } else {
    const nestedHashIndex = rest.indexOf("#", 1);
    if (nestedHashIndex !== -1) {
      pathname = rest.slice(0, nestedHashIndex);
      sectionHash = rest.slice(nestedHashIndex);
    }
  }

  if (!pathname.startsWith("/")) return null;
  return { pathname, search, hash: sectionHash };
}

/**
 * Resolve the in-page section id from HashRouter location.
 * Prefer nested hash (`#/glofox/authentication#rate-limiting` → `#rate-limiting`);
 * fall back to `?section=` for older bookmarks.
 */
export function getDocSectionId(
  search: string | undefined,
  hash: string | undefined,
): string | null {
  const fragment = (hash ?? "").trim();
  if (fragment.startsWith("#") && !fragment.startsWith("#/")) {
    const fromHash = decodeURIComponent(fragment.slice(1)).trim();
    if (fromHash) return fromHash;
  }

  if (!search) return null;
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  const fromQuery = params.get(DOC_SECTION_SEARCH_PARAM)?.trim();
  return fromQuery || null;
}

/** Strip `section` from a location search string (keep other params). */
export function stripDocSectionSearchParam(search: string | undefined): string {
  if (!search) return "";
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  params.delete(DOC_SECTION_SEARCH_PARAM);
  const next = params.toString();
  return next ? `?${next}` : "";
}

/** Path-only Markdoc asset URL (drop any `#fragment`). */
export function stripDocsAssetFragment(assetUrl: string): string {
  return assetUrl.split("#")[0] ?? assetUrl;
}

/**
 * Fragment id baked into a Markdoc asset URL (e.g. legacy
 * `api-reference.html#error-handling`), or null when absent.
 */
export function getDocsAssetFragment(assetUrl: string): string | null {
  const hashIndex = assetUrl.indexOf("#");
  if (hashIndex < 0) return null;
  const fragment = decodeURIComponent(assetUrl.slice(hashIndex + 1)).trim();
  return fragment || null;
}

/** Append `#section` to a Markdoc asset URL (iframe `src`). */
export function withDocsSectionFragment(
  assetUrl: string,
  sectionId: string | null,
): string {
  const base = stripDocsAssetFragment(assetUrl);
  if (!sectionId) return base;
  return `${base}#${sectionId}`;
}

export function isDocsFrameSectionMessage(
  data: unknown,
): data is DocsFrameSectionMessage {
  if (!data || typeof data !== "object") return false;
  const message = data as Record<string, unknown>;
  return (
    message.source === DOCS_FRAME_MESSAGE_SOURCE &&
    message.type === DOCS_FRAME_SECTION_MESSAGE_TYPE &&
    (message.id === null || typeof message.id === "string")
  );
}

export function isDocsFrameRouteMessage(
  data: unknown,
): data is DocsFrameRouteMessage {
  if (!data || typeof data !== "object") return false;
  const message = data as Record<string, unknown>;
  return (
    message.source === DOCS_FRAME_MESSAGE_SOURCE &&
    message.type === DOCS_FRAME_ROUTE_MESSAGE_TYPE &&
    typeof message.pathname === "string" &&
    message.pathname.startsWith("/") &&
    (message.search === undefined || typeof message.search === "string") &&
    (message.hash === undefined || typeof message.hash === "string")
  );
}

export function isTrustedDocsFrameMessage(
  event: MessageEvent,
  frames: readonly Pick<HTMLIFrameElement, "contentWindow">[],
): boolean {
  if (event.origin !== window.location.origin) {
    return false;
  }
  if (!frames.some((frame) => frame.contentWindow === event.source)) {
    return false;
  }
  return (
    isDocsFrameSectionMessage(event.data) ||
    isDocsFrameRouteMessage(event.data)
  );
}
